import { Link } from 'react-router-dom';
import { IconClipboardList } from '@tabler/icons-react';
import { useShellProfile } from '../../state/useShellProfile';
import { useListNotificationsQuery } from '../../state/domainRouterApi';
import { SupportErrorMessage } from '../../components/overlays/EngineeringSupportBanner';

const Empty = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-irongrey/50 font-medium">{children}</p>
);

const ErrorState = ({ children }: { children: React.ReactNode }) => (
  <SupportErrorMessage>{children}</SupportErrorMessage>
);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const readText = (value: unknown): string => String(value ?? '').trim();

const withQuery = (pathname: string, query: Record<string, string>): string => {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    const normalized = String(value ?? '').trim();
    if (!normalized) return;
    search.set(key, normalized);
  });
  const encoded = search.toString();
  return encoded ? `${pathname}?${encoded}` : pathname;
};

type NoticeRowProps = {
  notice: Record<string, unknown>;
  rowKey: string;
};

const NoticeRow = ({ notice, rowKey }: NoticeRowProps) => {
  const title = readText(notice.title ?? notice.summary ?? notice.category ?? 'Attendance');
  const body = readText(notice.body ?? notice.message);
  const at = readText(notice.createdAt ?? notice.occurredAt ?? notice.updatedAt);
  const moduleLabel = readText(notice.module);
  const noticeId = readText(notice.id) || rowKey;
  const target = withQuery('/parent/portal/attendance', { noticeId });

  return (
    <li className="px-2 py-2">
      <Link
        to={target}
        className="block rounded-xl px-4 py-3 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        <article className="flex items-start justify-between gap-4">
          <header className="min-w-0">
            <h2 className="text-sm font-bold text-irongrey truncate">{title}</h2>
            {body ? <p className="text-xs text-irongrey/60 font-medium mt-1">{body}</p> : null}
          </header>
          <p className="text-right">
            {moduleLabel ? (
              <span className="block text-[10px] font-bold uppercase tracking-widest text-irongrey/40">
                {moduleLabel}
              </span>
            ) : null}
            {at ? <time className="block text-xs text-irongrey/60 font-medium mt-1">{at}</time> : null}
          </p>
        </article>
      </Link>
    </li>
  );
};

export function PortalAttendancePage() {
  const { profile } = useShellProfile();
  const actorId = profile?.userSub ?? null;

  const notificationsQuery = useListNotificationsQuery(
    actorId ? { actorId, categories: ['attendance'], includeRead: true, limit: 50 } : {},
    { skip: !actorId },
  );

  const items = notificationsQuery.data?.items ?? [];

  return (
    <main
      data-testid="parent-portal-attendance"
      data-extracted-from="owned:ystnj/parent-portal/attendance"
      className="w-full space-y-6"
    >
      <header className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center" aria-hidden>
          <IconClipboardList size={20} className="text-primary" />
        </span>
        <section>
          <h1 className="text-2xl font-bold text-irongrey tracking-tight">Attendance</h1>
          <p className="text-sm text-irongrey/60 font-medium mt-1">Attendance-related updates.</p>
          <p className="sr-only">
            Attendance summary timeline with absences, check-ins, check-outs, late notices, and school-day reminders.
          </p>
        </section>
      </header>

      {!actorId ? <ErrorState>Unable to resolve your account identity.</ErrorState> : null}
      {notificationsQuery.isLoading ? <Empty>Loading attendance updates...</Empty> : null}
      {notificationsQuery.error ? <ErrorState>Unable to load attendance updates right now.</ErrorState> : null}

      <section className="bg-white border border-gray-100 rounded-3xl shadow-card overflow-hidden" aria-label="Attendance updates">
        {!notificationsQuery.isLoading && !notificationsQuery.error && items.length === 0 ? (
          <section className="px-6 py-6">
            <Empty>Attendance updates are syncing.</Empty>
          </section>
        ) : null}

        <ul className="divide-y divide-gray-100">
          {items.map((item, idx) => {
            const notice = asRecord(item);
            const key = `${readText(notice.id)}-${idx}`;
            return <NoticeRow key={key} notice={notice} rowKey={`${idx}`} />;
          })}
        </ul>
      </section>
    </main>
  );
}
